import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { INDICES_CHART_BUNDLE_URL } from '@constants/indicesChartBundle';
import type { IndicesChartBundlePayload, IndicesChartBundleReadyMessage } from '@app-types/indices';

interface IndicesChartFrameProps {
  payload: IndicesChartBundlePayload;
  title: string;
  className?: string;
  refreshRevision?: number;
}

const parseMessage = (data: unknown): unknown => {
  if (typeof data !== 'string' || !data) return null;
  try {
    return JSON.parse(data) as unknown;
  } catch {
    return null;
  }
};

const isReadyMessage = (value: unknown): value is IndicesChartBundleReadyMessage => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return candidate.source === 'labci-chart5' && candidate.message === 'ready';
};

const IndicesChartFrame: React.FC<IndicesChartFrameProps> = ({
  payload,
  title,
  className,
  refreshRevision = 0,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const lastPayloadSignatureRef = useRef<string | null>(null);
  const [readyRevision, setReadyRevision] = useState(0);
  const bundleOrigin = useMemo(
    () => new URL(INDICES_CHART_BUNDLE_URL, window.location.href).origin,
    [],
  );
  const payloadSignature = useMemo(
    () => JSON.stringify([payload, refreshRevision]),
    [payload, refreshRevision],
  );

  useLayoutEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== bundleOrigin) return;
      if (!isReadyMessage(parseMessage(event.data))) return;

      lastPayloadSignatureRef.current = null;
      setReadyRevision((revision) => revision + 1);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [bundleOrigin]);

  useEffect(() => {
    if (readyRevision === 0) return;
    const childWindow = iframeRef.current?.contentWindow;
    if (!childWindow || lastPayloadSignatureRef.current === payloadSignature) return;

    childWindow.postMessage(JSON.stringify(payload), bundleOrigin);
    lastPayloadSignatureRef.current = payloadSignature;
  }, [bundleOrigin, payload, payloadSignature, readyRevision]);

  return (
    <iframe
      ref={iframeRef}
      className={['indices-chart-frame', className ?? ''].filter(Boolean).join(' ')}
      src={INDICES_CHART_BUNDLE_URL}
      title={title}
      referrerPolicy="no-referrer"
      tabIndex={-1}
    />
  );
};

export default IndicesChartFrame;
